import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Plus, FileText, Filter, X, ArrowLeft, User, Award, Scissors } from 'lucide-react';
import ClientCard from '../components/ClientCard';
import MobileHeader from '../components/MobileHeader';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { ModuleContainer } from '../components/ui/ModuleContainer';
import { SearchInput } from '../components/ui/SearchInput';
import { FilterButton } from '../components/ui/FilterButton';
import { ActionButton } from '../components/ui/ActionButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FichasListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canCreateSheets, canDeleteSheets, canDownloadExpediente, user } = useAuth();
  
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(() => {
    // ?proximos=true (desde el Dashboard): eventos de hoy a 30 días, igual que el contador
    const proximos = searchParams.get('proximos') === 'true';
    const dia = (d) => new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);
    return {
      estado: searchParams.get('estado') || '',
      fecha_evento_desde: proximos ? dia(0) : '',
      fecha_evento_hasta: proximos ? dia(30) : ''
    };
  });

  useEffect(() => {
    fetchSheets();
  }, []);

  const fetchSheets = async (search = '', filterParams = filters) => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterParams.estado) params.estado = filterParams.estado;
      if (filterParams.fecha_evento_desde) params.fecha_evento_desde = filterParams.fecha_evento_desde;
      if (filterParams.fecha_evento_hasta) params.fecha_evento_hasta = filterParams.fecha_evento_hasta;
      
      const response = await axios.get(`${API_URL}/sheets`, { params });
      setSheets(response.data);
    } catch (error) {
      toast.error('Error al cargar las fichas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    if (searchValue.length >= 2 || searchValue.length === 0) {
      fetchSheets(searchValue, filters);
    }
  };

  const handleFilterChange = (field, newValue) => {
    const newFilters = { ...filters, [field]: newValue };
    setFilters(newFilters);
    fetchSheets(searchTerm, newFilters);
  };

  const clearFilters = () => {
    setFilters({
      estado: '',
      fecha_evento_desde: '',
      fecha_evento_hasta: ''
    });
    fetchSheets(searchTerm, {});
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta ficha?')) {
      try {
        await axios.delete(`${API_URL}/sheets/${id}`);
        toast.success('Ficha eliminada');
        fetchSheets(searchTerm, filters);
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API_URL}/sheets/${id}/duplicate`);
      toast.success('Ficha duplicada');
      fetchSheets(searchTerm, filters);
    } catch (error) {
      toast.error('Error al duplicar');
    }
  };

  const handleExportPDF = async (id, nombre) => {
    try {
      const response = await axios.get(`${API_URL}/sheets/${id}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ficha_${nombre}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al exportar PDF');
    }
  };

  const handleExportPDFInternal = async (id, nombre) => {
    try {
      const response = await axios.get(`${API_URL}/sheets/${id}/export/pdf-internal`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ficha_interna_${nombre}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF interno descargado');
    } catch (error) {
      toast.error('Error al exportar PDF interno');
    }
  };

  const handleExportExpediente = async (id, nombre) => {
    try {
      const response = await axios.get(`${API_URL}/sheets/${id}/export/pdf-expediente`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expediente_completo_${nombre}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Expediente completo descargado');
    } catch (error) {
      toast.error('Error al exportar expediente');
    }
  };

  const hasActiveFilters = filters.estado || filters.fecha_evento_desde || filters.fecha_evento_hasta;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <MobileHeader />
      
      {/* Desktop Header con Safe Area */}
      <header className="bg-black/95 backdrop-blur-xl fixed top-0 left-0 w-full z-50 hidden md:block border-b border-zinc-800 shadow-lg safe-area-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 py-3">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm">Dashboard</span>
              </button>
              <div className="w-px h-6 bg-white/20" />
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                  <Scissors className="w-4 h-4 text-white/70" />
                </div>
                <div>
                  <h1 className="font-serif text-lg text-white">Fichas de Clientes</h1>
                  <p className="text-[10px] text-white/50 uppercase tracking-wider">Gestión de medidas y pedidos</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/clientes')}
                className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/10 transition-all rounded-lg"
              >
                <User className="w-4 h-4" />
                <span>Clientes</span>
              </button>
              {(user?.role === 'gerencia' || user?.role === 'admin') && (
                <button
                  onClick={() => navigate('/vendedores')}
                  className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/10 transition-all rounded-lg"
                >
                  <Award className="w-4 h-4" />
                  <span>Vendedores</span>
                </button>
              )}
              {canCreateSheets() && (
                <ActionButton
                  onClick={() => navigate('/sheet/new')}
                  variant="secondary"
                  icon={Plus}
                  data-testid="new-sheet-btn"
                >
                  Nueva Ficha
                </ActionButton>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:pt-24">
        {/* Mobile Title */}
        <div className="md:hidden mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Volver al Dashboard</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center">
              <Scissors className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h1 className="font-serif text-2xl text-slate-900">Fichas de Clientes</h1>
              <p className="text-sm text-slate-500">
                {sheets.length} {sheets.length === 1 ? 'ficha registrada' : 'fichas registradas'}
              </p>
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <ModuleContainer variant="fichas" className="mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <SearchInput
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Buscar por nombre, cédula o teléfono..."
              className="flex-1"
            />

            <div className="flex gap-2">
              <FilterButton
                active={showFilters}
                count={hasActiveFilters ? 1 : 0}
                onClick={() => setShowFilters(!showFilters)}
                onClear={hasActiveFilters ? clearFilters : undefined}
              />

              {canCreateSheets() && (
                <ActionButton
                  onClick={() => navigate('/sheet/new')}
                  variant="primary"
                  icon={Plus}
                  className="md:hidden"
                >
                  Nueva
                </ActionButton>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 pt-5 border-t border-slate-200">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-medium">
                  Estado
                </label>
                <select
                  value={filters.estado}
                  onChange={(e) => handleFilterChange('estado', e.target.value)}
                  className="w-full h-11 bg-white border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10 transition-all"
                  data-testid="filter-estado"
                >
                  <option value="">Todos los estados</option>
                  <option value="borrador">Borrador</option>
                  <option value="finalizada">Finalizada</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-medium">
                  Evento desde
                </label>
                <input
                  type="date"
                  value={filters.fecha_evento_desde}
                  onChange={(e) => handleFilterChange('fecha_evento_desde', e.target.value)}
                  className="w-full h-11 bg-white border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10 transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-medium">
                  Evento hasta
                </label>
                <input
                  type="date"
                  value={filters.fecha_evento_hasta}
                  onChange={(e) => handleFilterChange('fecha_evento_hasta', e.target.value)}
                  className="w-full h-11 bg-white border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10 transition-all"
                />
              </div>
            </div>
          )}
        </ModuleContainer>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin mb-5" />
            <p className="text-xs uppercase tracking-wider text-slate-400">Cargando fichas...</p>
          </div>
        ) : sheets.length === 0 ? (
          <ModuleContainer variant="fichas">
            <EmptyState
              type={searchTerm ? 'search' : 'fichas'}
              title={searchTerm ? 'No se encontraron resultados' : 'No hay fichas registradas'}
              description={searchTerm 
                ? 'Intenta con otros términos de búsqueda o ajusta los filtros'
                : 'Comienza creando tu primera ficha de cliente para gestionar las medidas'
              }
              actionLabel={searchTerm ? 'Limpiar búsqueda' : (canCreateSheets() ? 'Crear primera ficha' : undefined)}
              onAction={searchTerm ? () => { setSearchTerm(''); fetchSheets('', filters); } : (canCreateSheets() ? () => navigate('/sheet/new') : undefined)}
            />
          </ModuleContainer>
        ) : (
          <div className="space-y-4">
            {/* Contador de resultados */}
            <div className="flex items-center justify-between px-1">
              <p className="text-sm text-slate-500">
                Mostrando <span className="font-medium text-slate-900">{sheets.length}</span> {sheets.length === 1 ? 'ficha' : 'fichas'}
                {searchTerm && <span> para "{searchTerm}"</span>}
              </p>
            </div>
            
            {sheets.map((sheet, index) => (
              <div 
                key={sheet.id}
                className="animate-fade-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <ClientCard
                  sheet={sheet}
                  onEdit={(id) => navigate(`/sheet/${id}`)}
                  onDuplicate={handleDuplicate}
                  onExportPDF={handleExportPDF}
                  onExportPDFInternal={handleExportPDFInternal}
                  onExportExpediente={handleExportExpediente}
                  onDelete={handleDelete}
                  canDuplicate={canCreateSheets()}
                  canDelete={canDeleteSheets()}
                  canExportExpediente={canDownloadExpediente()}
                />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default FichasListPage;
